/**
 * Tooth-numbering system conversion + grid layouts.
 *
 * Internal storage is always Universal (1-32) so historic cases, the CSV
 * importer, and the database schema stay backwards-compatible. The UI
 * converts to FDI for display when the user selects that system.
 *
 * Conventions (looking at the chart laid out on the page, NOT inside the
 * patient's mouth — same orientation as the user's reference screenshot):
 *
 *   Upper Left/I       |   Upper Right/II
 *   18 17 16 ... 11    |   21 22 ... 27 28
 *   ────────────────────────────────────────
 *   48 47 46 ... 41    |   31 32 ... 37 38
 *   Lower Left/IV      |   Lower Right/III
 *
 * The grid order goes Universal #1 (upper-RIGHT 3rd molar — patient's right,
 * which appears on the LEFT of the chart in the patient-facing convention
 * the user picked) through #16 across the upper row, then #17 (lower-LEFT
 * 3rd molar) through #32 across the lower row, sweeping right-to-left.
 */

// Hard-coded canonical mapping. Source: ISO-3950 (FDI) cross-walked to
// Universal Numbering System (UNS). 32 permanent teeth, no primary.
export const UNIVERSAL_TO_FDI = {
    // Upper Left/I quadrant (patient's right · FDI quadrant 1)
    1: 18, 2: 17, 3: 16, 4: 15, 5: 14, 6: 13, 7: 12, 8: 11,
    // Upper Right/II quadrant (patient's left · FDI quadrant 2)
    9: 21, 10: 22, 11: 23, 12: 24, 13: 25, 14: 26, 15: 27, 16: 28,
    // Lower Right/III quadrant (patient's left · FDI quadrant 3)
    17: 38, 18: 37, 19: 36, 20: 35, 21: 34, 22: 33, 23: 32, 24: 31,
    // Lower Left/IV quadrant (patient's right · FDI quadrant 4)
    25: 41, 26: 42, 27: 43, 28: 44, 29: 45, 30: 46, 31: 47, 32: 48,
};

export const FDI_TO_UNIVERSAL = Object.fromEntries(
    Object.entries(UNIVERSAL_TO_FDI).map(([u, f]) => [f, Number(u)])
);

/**
 * Convert a single tooth number from Universal → display system.
 * Returns the input untouched if it's already in the target system or out of range.
 */
export function toDisplay(toothNumber, system = "fdi") {
    if (system === "universal") return toothNumber;
    return UNIVERSAL_TO_FDI[toothNumber] ?? toothNumber;
}

/**
 * Render a list of tooth numbers using the chosen system.
 * Returns the comma-separated string the way the user expects to read it.
 */
export function formatTeeth(numbers, system = "fdi") {
    if (!Array.isArray(numbers) || numbers.length === 0) return "—";
    return numbers.map((n) => toDisplay(n, system)).join(", ");
}

/**
 * Grid layout for the picker — list of cells per visual position so the
 * component can render the same shape regardless of system. Each cell has:
 *   - universal:    the Universal index (storage key)
 *   - fdi:          the FDI label
 *   - row:          'upper' | 'lower'
 *   - side:         'left' | 'right' (visual side of the chart)
 */
export const TOOTH_GRID = {
    upper: {
        // Patient's right (chart's left) — FDI quadrant 1, Universal 1-8
        left: [1, 2, 3, 4, 5, 6, 7, 8].map((u) => ({ universal: u, fdi: UNIVERSAL_TO_FDI[u] })),
        // Patient's left (chart's right) — FDI quadrant 2, Universal 9-16
        right: [9, 10, 11, 12, 13, 14, 15, 16].map((u) => ({ universal: u, fdi: UNIVERSAL_TO_FDI[u] })),
    },
    lower: {
        // Patient's right (chart's left) — FDI quadrant 4, Universal 32→25 going right-to-left
        left: [32, 31, 30, 29, 28, 27, 26, 25].map((u) => ({ universal: u, fdi: UNIVERSAL_TO_FDI[u] })),
        // Patient's left (chart's right) — FDI quadrant 3, Universal 24→17
        right: [24, 23, 22, 21, 20, 19, 18, 17].map((u) => ({ universal: u, fdi: UNIVERSAL_TO_FDI[u] })),
    },
};

export const QUADRANT_LABELS = {
    upper_left: "Upper Left / I",
    upper_right: "Upper Right / II",
    lower_left: "Lower Left / IV",
    lower_right: "Lower Right / III",
};

export const SYSTEM_OPTIONS = [
    { value: "fdi", label: "FDI · ISO 3950" },
    { value: "universal", label: "Universal · 1-32" },
];

const PREF_KEY = "toothrocket.toothSystem";

export function getStoredToothSystem() {
    if (typeof window === "undefined") return "fdi";
    try {
        const v = window.localStorage.getItem(PREF_KEY);
        return v === "universal" ? "universal" : "fdi";
    } catch {
        return "fdi";
    }
}

export function setStoredToothSystem(system) {
    if (typeof window === "undefined") return;
    try {
        window.localStorage.setItem(PREF_KEY, system === "universal" ? "universal" : "fdi");
    } catch { /* localStorage may be disabled — preference falls back to FDI default */ }
}
